import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Mail, Clock, CreditCard, Lock, Shield, Mic } from 'lucide-react';

const SiteFooter = () => {
  const navigate = useNavigate();

  return (
    <footer className="py-16 bg-secondary/30 border-t border-border">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Widget 1: Brand + Description + Payment */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Mic className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl font-outfit text-foreground">VoiceraLab</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              VoiceraLab is the world's most advanced AI voice platform. With 204+ neural voices, 26+ languages, real-time voice cloning, and studio-quality output, we outperform every competitor. Our proprietary neural synthesis engine delivers the most natural, expressive speech available — trusted by thousands of creators, businesses, and developers worldwide.
            </p>
            <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wider">Secured Payments</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border">
                <Lock className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs font-medium text-foreground">SSL Secured</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border">
                <CreditCard className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-medium text-foreground">Stripe</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border">
                <Shield className="w-3.5 h-3.5 text-purple-500" />
                <span className="text-xs font-medium text-foreground">PCI DSS</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {["Visa","Mastercard","Amex","Discover"].map(c => (
                <span key={c} className="text-xs px-2 py-1 rounded bg-background border border-border text-muted-foreground">{c}</span>
              ))}
            </div>
          </div>

          {/* Widget 2: Site Links */}
          <div>
            <h3 className="font-semibold font-outfit text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { label: "Home", path: "/" },
                { label: "About", path: "/about" },
                { label: "Pricing", path: "/pricing" },
                { label: "Contact", path: "/contact" },
                { label: "Privacy Policy", path: "/privacy" },
                { label: "Terms of Service", path: "/terms" },
              ].map(link => (
                <li key={link.label}>
                  <button onClick={() => navigate(link.path)} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Widget 3: Contact Details */}
          <div>
            <h3 className="font-semibold font-outfit text-foreground mb-4">Contact Details</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">USA Address</p>
                  <p className="text-sm text-muted-foreground">8301 State Line Rd Ste 220<br/>Kansas City, MO 64114</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Working Hours</p>
                  <p className="text-sm text-muted-foreground">24/7 Support</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Email</p>
                  <p className="text-sm text-muted-foreground">hello@voiceralab.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} VoiceraLab. All rights reserved.</p>
          <div className="flex gap-4">
            <button onClick={() => navigate('/privacy')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy</button>
            <button onClick={() => navigate('/terms')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms</button>
            <button onClick={() => navigate('/contact')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Contact</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
